import AddIcon from '@offline-mui/icons-material/Add'
import ArrowBackIcon from '@offline-mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@offline-mui/icons-material/DeleteOutlined'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@offline-mui/material'
import { useEffect, useState } from 'react'
import { accountingRequest, jsonInit } from '../api'
import { LeoHeader } from '../LeoHeader'

const EMPTY_ACCOUNT = { iata: '', bsp: '', pcc: '', pccSup: '' }

function HeaderCells({ labels, color = 'info.main' }: { labels: string[]; color?: string }) {
  return (
    <TableHead>
      <TableRow sx={{ bgcolor: color }}>
        {labels.map((label, index) => (
          <TableCell key={`${label}-${index}`} sx={{ color: 'common.white', fontWeight: 700, border: 0 }}>{label}</TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

function SectionTitle({ children, color = 'primary.main' }: { children: string; color?: string }) {
  return (
    <Typography variant="overline" color={color} sx={{ display: 'block', fontWeight: 700, mb: 1 }}>
      {children}
    </Typography>
  )
}

function OfficeList({ offices, onOpen, opening }: any) {
  return (
    <TableContainer component={Card} sx={{ mt: 3 }}>
      {opening ? <LinearProgress color="warning" /> : null}
      <Table size="small">
        <HeaderCells labels={['Office code', 'Office name', 'City', 'IATA accounts']} />
        <TableBody>
          {offices.map((office: any, index: number) => {
            const isOpening = opening === office.code
            return (
              <TableRow
                key={office.code}
                aria-busy={isOpening || undefined}
                sx={{
                  bgcolor: isOpening ? 'rgba(233, 180, 76, 0.22)' : index % 2 ? 'rgba(29, 78, 137, 0.06)' : 'background.paper',
                  opacity: opening && !isOpening ? 0.5 : 1,
                  transition: 'background-color 120ms, opacity 120ms',
                }}
              >
                <TableCell>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    disabled={Boolean(opening)}
                    onClick={() => onOpen(office.code)}
                    sx={{ fontWeight: 700, '&.Mui-disabled': { color: 'text.disabled' } }}
                  >
                    {office.code}
                  </Link>
                  {isOpening ? (
                    <Typography component="span" variant="caption" color="warning.dark" sx={{ ml: 1, fontWeight: 600 }}>
                      Loading…
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{office.name}</TableCell>
                <TableCell>{office.city}</TableCell>
                <TableCell>{office.iataCount}</TableCell>
              </TableRow>
            )
          })}
          {offices.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="body2" color="text.secondary">No offices yet.</Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function OfficeForm({ office, options, onBack, onSaved, onDeleted }: any) {
  const [draft, setDraft] = useState<any>(office)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState('')
  const [savedAt, setSavedAt] = useState('')

  useEffect(() => {
    setDraft(office)
    setError('')
    setSavedAt('')
    setConfirmDelete(false)
  }, [office])

  const set = (key: string) => (event: any) => setDraft((current: any) => ({ ...current, [key]: event.target.value }))
  const setAccount = (index: number, key: string) => (event: any) =>
    setDraft((current: any) => ({
      ...current,
      accounts: current.accounts.map((row: any, rowIndex: number) => (rowIndex === index ? { ...row, [key]: event.target.value } : row)),
    }))
  const addAccount = () => setDraft((current: any) => ({ ...current, accounts: [...current.accounts, { ...EMPTY_ACCOUNT }] }))
  const removeAccount = (index: number) =>
    setDraft((current: any) => ({ ...current, accounts: current.accounts.filter((_: any, rowIndex: number) => rowIndex !== index) }))

  async function save(event: any) {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      const body = await accountingRequest(`/offices/${draft.code}`, jsonInit('PUT', draft))
      setSavedAt(new Date().toLocaleTimeString())
      onSaved(body)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  // First click arms the button, second click deletes. An office that invoices or runs use is refused by the server.
  async function remove() {
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    setSaving(true)
    setError('')
    try {
      await accountingRequest(`/offices/${encodeURIComponent(office.code)}`, { method: 'DELETE' })
      onDeleted(office.code)
    } catch (err: any) {
      setError(err.message)
      setConfirmDelete(false)
    } finally {
      setSaving(false)
    }
  }

  const field = (key: string, label: string, extra: any = {}) => (
    <TextField size="small" fullWidth label={label} value={draft[key] ?? ''} onChange={set(key)} {...extra} />
  )
  const select = (key: string, label: string, values: string[]) => (
    <TextField select size="small" fullWidth label={label} value={draft[key] ?? ''} onChange={set(key)}>
      <MenuItem value="">—</MenuItem>
      {values.map((value) => (
        <MenuItem key={value} value={value}>{value}</MenuItem>
      ))}
    </TextField>
  )

  return (
    <Box component="form" onSubmit={save} sx={{ mt: 3 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
        <IconButton onClick={onBack} aria-label="Back to offices"><ArrowBackIcon /></IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Office {draft.code}</Typography>
        <Typography variant="body2" color="text.secondary">{office.name}</Typography>
        <Box sx={{ flex: 1 }} />
        {savedAt ? <Typography variant="caption" color="success.main">Saved {savedAt}</Typography> : null}
        <Button type="button" color="error" variant={confirmDelete ? 'contained' : 'text'} disabled={saving} onClick={remove}>
          {confirmDelete ? `Confirm delete ${office.code}` : 'Delete'}
        </Button>
        <Button type="submit" variant="contained" color="warning" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </Stack>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Card sx={{ borderTop: '4px solid', borderTopColor: 'primary.main' }}>
        <CardContent>
          <SectionTitle>Office</SectionTitle>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Stack spacing={2}>
              <TextField size="small" fullWidth label="Office code" value={draft.code} slotProps={{ input: { readOnly: true } }} />
              {field('name', 'Name', { required: true })}
              {field('address', 'Address')}
              {field('city', 'City')}
              {field('postalCode', 'Postal code')}
            </Stack>
            <Stack spacing={2}>
              {field('phone', 'Phone')}
              {field('fax', 'Fax')}
              {field('email', 'General e-mail', { type: 'email' })}
              {select('language', 'Language', options.languages)}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderTop: '4px solid', borderTopColor: 'info.main' }}>
        <CardContent>
          <Stack direction="row" sx={{ alignItems: 'center', mb: 1 }}>
            <SectionTitle color="info.main">IATA accounts</SectionTitle>
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<AddIcon />} onClick={addAccount}>Add IATA</Button>
          </Stack>
          <TableContainer>
            <Table size="small">
              <HeaderCells labels={['#', 'IATA', 'BSP', 'PCC', 'PCC sup', '']} />
              <TableBody>
                {draft.accounts.map((account: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontWeight: 700, width: 40 }}>{index + 1}</TableCell>
                    <TableCell><TextField size="small" fullWidth value={account.iata} onChange={setAccount(index, 'iata')} /></TableCell>
                    <TableCell sx={{ width: 120 }}><TextField size="small" fullWidth value={account.bsp} onChange={setAccount(index, 'bsp')} /></TableCell>
                    <TableCell sx={{ width: 140 }}><TextField size="small" fullWidth value={account.pcc} onChange={setAccount(index, 'pcc')} /></TableCell>
                    <TableCell sx={{ width: 140 }}><TextField size="small" fullWidth value={account.pccSup} onChange={setAccount(index, 'pccSup')} /></TableCell>
                    <TableCell sx={{ width: 48 }}>
                      <IconButton size="small" aria-label="Remove IATA" onClick={() => removeAccount(index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {draft.accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No IATA accounts. Use Add IATA.</Typography></TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderTop: '4px solid', borderTopColor: 'warning.main' }}>
        <CardContent>
          <SectionTitle color="warning.dark">Invoicing</SectionTitle>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Stack spacing={2}>
              {field('lastInvoice', 'Last invoice')}
              {select('defaultPrinter', 'Default printer', options.printers)}
              {select('defaultFax', 'Default FAX', options.printers)}
            </Stack>
            <Stack spacing={2}>
              {field('ticketingAgent', 'Ticketing agent')}
              {field('invoicingAgent', 'Invoicing agent')}
              {field('reservationAgent', 'Reservation agent')}
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default function OfficesPage() {
  const [offices, setOffices] = useState<any[]>([])
  const [options, setOptions] = useState<{ languages: string[]; printers: string[] }>({ languages: [], printers: [] })
  const [office, setOffice] = useState<any>(null)
  const [opening, setOpening] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(true)

  async function loadList() {
    const body = await accountingRequest('/offices')
    setOffices(body.offices || [])
    setOptions({ languages: body.languages || [], printers: body.printers || [] })
  }

  useEffect(() => {
    loadList().catch((err) => setError(err.message)).finally(() => setLoading(false))
  }, [])

  async function openOffice(code: string) {
    if (opening) return
    setError('')
    setNotice('')
    setOpening(code)
    try {
      setOffice(await accountingRequest(`/offices/${code}`))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setOpening('')
    }
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 2 }}>
      <LeoHeader sx={{ mb: 3 }} />
      <Typography variant="h6" sx={{ fontWeight: 700 }}>Offices</Typography>
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      {notice ? <Alert severity="success" sx={{ mt: 2 }} onClose={() => setNotice('')}>{notice}</Alert> : null}
      {loading ? <Typography sx={{ mt: 2 }} color="info.main">Loading offices…</Typography> : null}
      {!loading && !office ? <OfficeList offices={offices} onOpen={openOffice} opening={opening} /> : null}
      {office ? (
        <OfficeForm
          office={office}
          options={options}
          onBack={() => setOffice(null)}
          onSaved={(saved: any) => {
            setOffice(saved)
            loadList().catch((err) => setError(err.message))
          }}
          onDeleted={(code: string) => {
            setOffice(null)
            setNotice(`Office ${code} deleted.`)
            loadList().catch((err) => setError(err.message))
          }}
        />
      ) : null}
    </Box>
  )
}
